import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * DocumentDiffRenderer — wysiwyg-deep-edit.
 *
 * Expects { edits: [{ block_id?, before?, after, reason?, type? }],
 * analysis_summary? }. Shows analysis at the top + a list of before/after
 * cards per edit.
 */

interface Edit {
  block_id?: string
  type?: string
  before?: string
  after?: string
  reason?: string
  confidence?: number
}

interface DocDiff {
  edits?: Edit[]
  analysis_summary?: string
  overall_score?: number
}

function pick(result: any): DocDiff | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (src && (Array.isArray(src.edits) || typeof src.analysis_summary === 'string')) {
      return src as DocDiff
    }
  }
  return null
}

export default function DocumentDiffRenderer(props: AgentOutputProps) {
  const diff = useMemo(() => pick(props.result), [props.result])

  if (!diff) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No document edits found in the agent result.
      </div>
    )
  }

  const edits = diff.edits || []

  return (
    <div className="space-y-3">
      {diff.analysis_summary && (
        <div className="border border-border rounded-md p-3 bg-muted/20">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">
            Analysis
          </div>
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
            {diff.analysis_summary}
          </div>
          {typeof diff.overall_score === 'number' && (
            <div className="text-xs text-muted-foreground mt-2">
              Overall quality: <b className="text-foreground">{diff.overall_score.toFixed(1)}/10</b>
            </div>
          )}
        </div>
      )}

      {edits.length > 0 ? (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
            {edits.length} edit{edits.length === 1 ? '' : 's'}
          </div>
          <div className="space-y-3">
            {edits.map((e, i) => (
              <div key={i} className="border border-border rounded-md overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b border-border text-xs">
                  {e.block_id && <span className="font-mono text-muted-foreground">{e.block_id}</span>}
                  {e.type && <span className="text-primary font-semibold">{e.type}</span>}
                  {typeof e.confidence === 'number' && (
                    <span className="ml-auto text-muted-foreground">
                      {(e.confidence * 100).toFixed(0)}% conf.
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {e.before !== undefined && (
                    <div className="p-3 border-b md:border-b-0 md:border-r border-border" style={{ background: '#EF444408' }}>
                      <div className="text-[10px] uppercase tracking-wide text-destructive font-semibold mb-1">Before</div>
                      <div className="text-sm text-foreground whitespace-pre-wrap">{e.before}</div>
                    </div>
                  )}
                  {e.after !== undefined && (
                    <div className="p-3" style={{ background: '#10B98108' }}>
                      <div className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#10B981' }}>
                        After
                      </div>
                      <div className="text-sm text-foreground whitespace-pre-wrap">{e.after}</div>
                    </div>
                  )}
                </div>
                {e.reason && (
                  <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground italic">
                    — {e.reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground italic">No edits returned.</div>
      )}
    </div>
  )
}
